/**
 * Pure view models for the two overview charts. No React, no fetching, no clock.
 *
 * Kept apart from `overviewSelectors` because these answer a different question: that
 * file decides what ONE number means, this one shapes a SERIES for a chart library.
 * Both are plain functions of their arguments, so the rules can be tested without
 * rendering an SVG — and the chart components stay free of arithmetic.
 *
 * NOTHING HERE FETCHES. Both series come from responses `useOverviewData` already
 * holds: the revenue trend is `revenue-analytics.trend`, which the same request that
 * feeds the KPI cards returns, and the cost structure is the A2 breakdown already
 * loaded for the "Betriebskosten" card. There is no second request for either — and
 * emphatically not one request per month, which is the other way to get a trend
 * wrong.
 *
 * P3a made the backend's `trend_months` default to the WINDOW LENGTH, so a
 * three-month selection returns three points and a twelve-month one twelve. The
 * six-month run-up under a SINGLE month is the one explicit value the hook still
 * sends, because a one-point chart is not a trend.
 */
import { periodKey, toNumber } from "./overviewSelectors";
// The month labels and the series palette already exist for the revenue-analytics
// page, which charts these same two quantities. Importing them keeps ONE chart
// vocabulary; a local copy is how two screens end up disagreeing about the colour of
// "Fakturiert" or the spelling of "Mrz".
import {
  SERIES_COLOR,
  shortPeriodLabel,
} from "../../../pages/admin/revenueAnalytics/revenueAnalyticsFormat";

/**
 * The two trend series, exported so the chart, its legend and its accessible table
 * are provably reading ONE set of names.
 *
 * WHY "FAKTURIERT" ALONE WOULD BE A LIE HERE.
 * The KPI card labelled "Fakturiert" is the A1 company figure: every invoice whose
 * `issue_date` falls in the selected month, no mapping required. `trend[].invoiced_total`
 * is a different population on a different axis — only invoices the draft ledger
 * authoritatively attributes to that RENT month. The two are not the same quantity and
 * in production the second is a minority of the first, so the series carries the axis
 * in its own name rather than in a caption the tooltip would never show.
 *
 * `paid_current` is deliberately NOT a third line. It is as-of-now for every point —
 * paying a year-old invoice today moves that year-old point — which needs the sentence
 * of explanation the revenue-analytics page gives it. On a summary card, beside a KPI
 * named "Zahlungseingang" that means money received IN the month, a line called
 * "Bezahlt" would read as that other axis. One caveat per chart is the budget.
 *
 * Colours come from the revenue-analytics palette rather than a second set defined
 * here: recharts needs a paint value and the two surfaces show the same two
 * quantities, so they should not disagree about their colour.
 */
export const TREND_SERIES = [
  {
    key: "expected",
    name: "Erwarteter Umsatz",
    color: SERIES_COLOR.expected,
    dash: undefined,
  },
  {
    key: "invoiced",
    name: "Fakturiert nach Leistungsmonat",
    color: SERIES_COLOR.invoiced,
    dash: "5 4",
  },
];

/**
 * The five operating buckets, in the order the A2 contract lists them.
 *
 * German labels, never the wire keys: `deposit_insurance_chf` is "Kautionsversicherung"
 * on screen. There is no "other" and no "Sonstiges" — A2a classifies fail-closed, and a
 * catch-all bucket is exactly the legacy behaviour that made `summary.total_costs`
 * untrustworthy.
 *
 * Hex rather than Tailwind classes for the same reason `SERIES_COLOR` uses them: a
 * chart library needs a paint value, not a class name. Chosen from the palette the app
 * already draws from and kept clear of the trend's blues, so a cost slice is never
 * mistaken for a revenue line; each reads on both the light and the dark card.
 */
export const COST_SEGMENTS = [
  { key: "rent", field: "rent_chf", name: "Miete", color: "#f59e0b" },
  { key: "utilities", field: "utilities_chf", name: "Nebenkosten", color: "#0ea5e9" },
  { key: "internet", field: "internet_chf", name: "Internet", color: "#a855f7" },
  { key: "cleaning", field: "cleaning_chf", name: "Reinigung", color: "#14b8a6" },
  {
    key: "deposit_insurance",
    field: "deposit_insurance_chf",
    name: "Kautionsversicherung",
    color: "#f43f5e",
  },
];

/**
 * `revenue-analytics.trend` → chart rows.
 *
 * A MISSING FIGURE STAYS MISSING. `toNumber` yields null for anything unusable, and
 * null is what recharts needs to leave a gap in the line; mapping it to 0 would draw a
 * month down to the axis and claim a zero nobody measured. An explicit `"0.00"` from
 * the contract is a different thing and survives as a real 0 — the backend documents
 * it as "nothing is PROVEN to belong to this month".
 *
 * No month is invented either: the row set is exactly what `trend` contains. If the
 * response carries six points this returns six, and a seventh is not filled in.
 *
 * NOTHING IS HIGHLIGHTED UNDER A RANGE (P3b). For a single month the series is a
 * six-month run-up and exactly one of its points is the selection, so that point is
 * marked. For a window the series IS the selection — every point is inside it — and
 * marking one of them would claim a month the user did not single out. The window
 * itself decides: `month_count > 1` means no highlight.
 *
 * The highlighted point is not assumed to be the last one either. Each row is
 * compared against the selected month by its own year/month, so a shorter or
 * reordered window cannot mislabel it.
 */
export function selectRevenueTrend(revenueAnalytics, periodWindow) {
  const points = Array.isArray(revenueAnalytics?.trend) ? revenueAnalytics.trend : [];
  const selected =
    periodWindow && !periodWindow.isRange
      ? periodKey({ year: periodWindow.year, month: periodWindow.month })
      : "";
  return points.map((p) => ({
    key: periodKey(p),
    label: shortPeriodLabel(p),
    isSelected: Boolean(selected) && periodKey(p) === selected,
    expected: toNumber(p?.expected_total),
    invoiced: toNumber(p?.invoiced_total),
    // The untouched contract values, so the tooltip prints the Decimal string the
    // server sent instead of a re-formatted float.
    raw: { expected: p?.expected_total, invoiced: p?.invoiced_total },
  }));
}

/**
 * The A2 cost breakdown → a donut view model.
 *
 * `total` is the CONTRACT's `operating_costs.total_chf`, never a sum of the five
 * segments: the schema rounds every figure independently, so adding the rounded parts
 * can miss the true total by a cent. Shares divide by that same contract total, which
 * is also the figure printed in the middle of the ring — so the segments visibly
 * account for the whole.
 *
 * UNCLASSIFIED IS RETURNED, NEVER MERGED. It is not a sixth bucket and must not enter
 * the ring or the total: A2a's entire point is that an unrecognised `cost_type` is
 * reported apart rather than folded into an operating figure the way the legacy KPI
 * path does. It comes back as its own object for the card to render beside the ring.
 */
export function selectCostStructure(costBreakdown) {
  const operating = costBreakdown?.operating_costs;
  const total = toNumber(operating?.total_chf);
  const divisible = total !== null && total > 0;

  const segments = COST_SEGMENTS.map((s) => {
    const value = toNumber(operating?.[s.field]);
    return {
      ...s,
      value,
      raw: operating?.[s.field],
      // No percentage without a denominator: a zero or missing total yields null and
      // the card omits the share rather than printing "0.0 %" or "NaN %".
      share: divisible && value !== null ? (value / total) * 100 : null,
    };
  });

  const unclassifiedTotal = toNumber(
    costBreakdown?.unclassified?.total_monthly_equivalent_chf
  );
  const unclassifiedCount = toNumber(costBreakdown?.unclassified?.cost_row_count);

  return {
    total,
    raw: { total: operating?.total_chf },
    segments,
    hasAmount: divisible,
    unclassified: {
      total: unclassifiedTotal,
      raw: costBreakdown?.unclassified?.total_monthly_equivalent_chf,
      count: unclassifiedCount === null ? 0 : unclassifiedCount,
      // Shown only when there is something to show. A portfolio whose every cost row
      // classifies gets no warning, because nothing is wrong.
      present: unclassifiedTotal !== null && unclassifiedTotal > 0,
    },
  };
}
